import { gql, useMutation, useQuery } from "@apollo/client";
import {
  Button,
  FormControl,
  FormLabel,
  Grid,
  OutlinedInput,
  Stack,
} from "@mui/material";
import { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ConditionalRenderer,
  useLanguageTranslator,
} from "../../../../../components";
import { useAuthSession } from "../../../../../components/AuthSessionProvider";
import { useNotify } from "../../../../../components/@common/NotificationsProvider";
import { toastHandler } from "../../../../../utils/helpers";
import { toastTypesKeys } from "../../../../../utils/constants";
import { ToastContainer } from "react-toastify";

const CREATE_INDICATOR_CATEGORY = gql`
  mutation indicatorCategoryCreate($input: IndicatorCategoryCreateInput!) {
    indicatorCategoryCreate(input: $input) {
      clientMutationId
      indicatorCategory {
        id
        name
        createdAt
        updatedAt
      }
    }
  }
`;

const GET_INDICATOR_CATEGORIES = gql`
  query GetIndicatorCategory($id: ID!) {
    indicatorCategory(id: $id) {
      id
      name
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_INDICATORS_CATEGORY = gql`
  mutation indicatorCategoryUpdate($id: ID!, $name: String!) {
    indicatorCategoryUpdate(
      input: { id: $id, indicatorCategoryInput: { name: $name } }
    ) {
      clientMutationId
      indicatorCategory {
        id
        name
        createdAt
        updatedAt
      }
    }
  }
`;

function IndicatorsCategoriesAction({ isUpdate = false }) {
  const { user } = useAuthSession() as any;
  const notify = useNotify();
  const navigate = useNavigate();
  const { translator, currentLanguage } = useLanguageTranslator();

  const [searchParams] = useSearchParams();
  const indicator_category_id = searchParams.get("id");

  const { data: indicatorsCategoryData } = useQuery(GET_INDICATOR_CATEGORIES, {
    variables: { id: indicator_category_id },
    skip: !indicator_category_id,
    context: {
      headers: {
        "access-token": user?.accessToken,
        client: user?.client,
        uid: user?.email,
      },
    },
  });

  const [createIndicatorCategory, { loading, error }] = useMutation(
    CREATE_INDICATOR_CATEGORY,
    {
      context: {
        headers: {
          "accept-language": currentLanguage || "en",
          "access-token": user?.accessToken,
          client: user?.client,
          uid: user?.email,
        },
      },
    }
  );

  const [updateIndicatorCategory] = useMutation(UPDATE_INDICATORS_CATEGORY, {
    context: {
      headers: {
        "accept-language": currentLanguage || "en",
        "access-token": user?.accessToken,
        client: user?.client,
        uid: user?.email,
      },
    },
  });

  const initialValues: any = useMemo(() => {
    if (!indicatorsCategoryData?.indicatorCategory) return undefined;

    const { name } = indicatorsCategoryData?.indicatorCategory;

    return {
      indicator_category_name: name,
    };
  }, [indicatorsCategoryData]);

  const form = useForm<any>({
    defaultValues: {
      indicator_category_name: "",
    },
    values: initialValues,
  });

  const handleFinish = useCallback(async (data: any, actionType: any) => {
    if (actionType === "update") {
      try {
        const response = await updateIndicatorCategory({
          variables: {
            id: Number(indicator_category_id),
            name: data?.indicator_category_name,
          },
        });
        if (response.data) {
          notify.success(`Indicator Category Updated successfuly!`);
          setTimeout(() => {
            navigate("/admin/indicators-categories");
            return;
          }, 1000);
        }
      } catch (err) {}
      return;
    }

    try {
      const response = await createIndicatorCategory({
        variables: {
          input: {
            indicatorCategoryInput: { name: data?.indicator_category_name },
          },
        },
      });
      if (actionType === "save") {
        if (response.data) {
          notify.success(`Indicator Category Created successfuly!`);
          setTimeout(() => {
            navigate("/admin/indicators-categories");
            return;
          }, 1000);
        }
      } else if (actionType === "saveAndAddNew") {
        if (response.data) {
          notify.success(`Indicator Category Created successfuly!`);
          setTimeout(() => {
            form?.reset();
            return;
          }, 1000);
        }
      }
    } catch (err) {
      const errorCatch = JSON.stringify(err);
      const parsedError = JSON.parse(errorCatch);

      toastHandler(parsedError?.message || "", toastTypesKeys.error);
    }
  }, []);

  return (
    <>
      <ToastContainer />
      <form onSubmit={form.handleSubmit((data) => handleFinish(data, "save"))}>
        <Grid
          container
          px={4}
          sx={{ backgroundColor: "#ffffff", borderRadius: "10px" }}
          spacing={2}
        >
          <Grid item xs={12} sm={6} lg={6}>
            <FormControl required disabled={false} fullWidth margin="normal">
              <FormLabel>{translator(`Indicators Category Name`)}</FormLabel>
              <OutlinedInput
                {...form?.register("indicator_category_name", {
                  required: "Required!",
                })}
              />
              {/* <FormHelperText>{errors.title?.message}</FormHelperText> */}
            </FormControl>
          </Grid>
        </Grid>
        <Stack
          sx={{ backgroundColor: "#ffffff" }}
          direction={"row"}
          justifyContent={"flex-end"}
          gap={2}
          px={4}
          py={4}
        >
          <Link
            to="/admin/indicators-categories"
            onClick={() => form?.reset()}
            style={{
              backgroundColor: "#e0e0e0",
              padding: "8px 12px",
              color: "#989C9F",
              borderRadius: "10px",
              fontSize: "14px",
              textDecoration: "none",
            }}
          >
            {translator("Cancel")}
          </Link>
          <Button
            type="button"
            onClick={form.handleSubmit((data) =>
              handleFinish(data, isUpdate ? "update" : "save")
            )}
            style={{
              backgroundColor: "#00AAB0",
              padding: "8px 12px",
              color: "white",
              borderRadius: "10px",
              fontSize: "14px",
              textDecoration: "none",
            }}
          >
            {isUpdate
              ? translator("generic.Update")
              : translator("generic.Save")}
          </Button>
          <ConditionalRenderer condition={!isUpdate}>
            <Button
              type="button"
              onClick={form.handleSubmit((data) =>
                handleFinish(data, "saveAndAddNew")
              )}
              style={{
                backgroundColor: "#F6A000",
                padding: "8px 12px",
                color: "white",
                borderRadius: "10px",
                fontSize: "14px",
                textDecoration: "none",
              }}
            >
              {translator("generic.Save")} & {translator("generic.Add New")}
            </Button>
          </ConditionalRenderer>
        </Stack>
      </form>
    </>
  );
}

export { IndicatorsCategoriesAction };
